/**
 * Account menu — the header's top-right control.
 *
 * Replaces the old flat row (address + role chips + two ghost buttons) with an
 * avatar button that opens a popover. The header keeps one control instead of
 * four, and the popover has room for the things that used to have nowhere to go:
 * the full name, the role explanation, the design switch.
 *
 * Interaction contract (all of it expected of a menu, none of it free):
 *   - click outside / Escape closes and returns focus to the trigger;
 *   - ArrowDown / ArrowUp move between items, Home / End jump to the ends;
 *   - opening with the keyboard (Enter, Space, ArrowDown) focuses the first item,
 *     opening with the mouse does not steal focus into the list;
 *   - `aria-expanded` + `aria-haspopup="menu"` on the trigger, `role="menu"` on
 *     the popover, so it is announced as a menu and not as a random panel.
 *
 * The menu is NOT rendered during a forced password change: Layout withholds it
 * and keeps only "Abmelden" reachable (see the rules at the top of Layout.tsx).
 */

import {
  useCallback,
  useEffect,
  useId,
  useRef,
  useState,
  type KeyboardEvent as ReactKeyboardEvent,
  type ReactNode,
} from 'react';
import { useLocation } from 'react-router-dom';

import { useAuth } from '../lib/auth';
import { ROLE_DESCRIPTIONS, ROLE_LABELS } from '../lib/types';
import Spinner from './Spinner';
import { ThemePreferenceControl, ThemeToggle } from './ThemeToggle';

/**
 * Up to two initials from the display name, falling back to the local part of
 * the address. Deliberately not a photo: there is no avatar upload, and a
 * generated cartoon would be noise.
 */
function initialsOf(name: string | null, email: string): string {
  const source = (name ?? '').trim() || email.split('@')[0]?.replace(/[._-]+/g, ' ') || '?';
  const parts = source.split(/\s+/).filter(Boolean);
  if (parts.length === 0) return '?';
  const first = parts[0]![0] ?? '?';
  const last = parts.length > 1 ? (parts[parts.length - 1]![0] ?? '') : '';
  return (first + last).toUpperCase();
}

export interface UserMenuProps {
  /** Opens the self-service password dialog. */
  onChangePassword: () => void;
  onLogout: () => void;
  loggingOut: boolean;
}

export function UserMenu({ onChangePassword, onLogout, loggingOut }: UserMenuProps): ReactNode {
  const { user } = useAuth();
  const [open, setOpen] = useState(false);
  const rootRef = useRef<HTMLDivElement | null>(null);
  const triggerRef = useRef<HTMLButtonElement | null>(null);
  const menuRef = useRef<HTMLDivElement | null>(null);
  // Set when the menu was opened from the keyboard: only then is focus moved
  // into the list on mount.
  const autoFocusRef = useRef(false);
  const menuId = useId();
  const location = useLocation();

  const close = useCallback((refocus: boolean): void => {
    setOpen(false);
    if (refocus) triggerRef.current?.focus();
  }, []);

  // Outside click (pointerdown, so a drag started outside also closes) and
  // Escape from anywhere in the subtree.
  useEffect(() => {
    if (!open) return;
    const onPointerDown = (event: PointerEvent): void => {
      if (!rootRef.current?.contains(event.target as Node)) setOpen(false);
    };
    const onKeyDown = (event: KeyboardEvent): void => {
      if (event.key === 'Escape') {
        event.stopPropagation();
        close(true);
      }
    };
    document.addEventListener('pointerdown', onPointerDown, true);
    document.addEventListener('keydown', onKeyDown);
    return () => {
      document.removeEventListener('pointerdown', onPointerDown, true);
      document.removeEventListener('keydown', onKeyDown);
    };
  }, [open, close]);

  // Close on resize so a breakpoint change cannot leave the popover covering
  // its new header position.
  useEffect(() => {
    if (!open) return;
    const onResize = (): void => setOpen(false);
    window.addEventListener('resize', onResize);
    return () => window.removeEventListener('resize', onResize);
  }, [open]);

  // A navigation must not leave the popover hanging over the new page. Every
  // menu entry closes it explicitly, but the browser's back button does not.
  useEffect(() => {
    setOpen(false);
  }, [location.pathname]);

  useEffect(() => {
    if (!open || !autoFocusRef.current) return;
    autoFocusRef.current = false;
    menuRef.current?.querySelector<HTMLElement>('[role="menuitem"]')?.focus();
  }, [open]);

  /** Roving focus across the enabled items. */
  const onMenuKeyDown = useCallback((event: ReactKeyboardEvent<HTMLDivElement>): void => {
    const keys = ['ArrowDown', 'ArrowUp', 'Home', 'End'];
    if (!keys.includes(event.key)) return;
    const items = Array.from(
      menuRef.current?.querySelectorAll<HTMLElement>('[role="menuitem"]:not([disabled])') ?? [],
    );
    if (items.length === 0) return;
    event.preventDefault();
    const current = items.indexOf(document.activeElement as HTMLElement);
    const next =
      event.key === 'Home'
        ? 0
        : event.key === 'End'
          ? items.length - 1
          : event.key === 'ArrowDown'
            ? (current + 1 + items.length) % items.length
            : (current - 1 + items.length) % items.length;
    items[next]?.focus();
  }, []);

  const onTriggerKeyDown = useCallback((event: ReactKeyboardEvent<HTMLButtonElement>): void => {
    if (event.key === 'ArrowDown' || event.key === 'Enter' || event.key === ' ') {
      event.preventDefault();
      autoFocusRef.current = true;
      setOpen(true);
    }
  }, []);

  if (!user) return null;

  const initials = initialsOf(user.name, user.email);
  const primaryLine = user.name?.trim() || user.email;

  return (
    <div className="user-menu" ref={rootRef}>
      {/* Outside the popover: one click, no menu to open — the switch people
          reach for most often should not be two interactions deep. */}
      <ThemeToggle />

      <button
        type="button"
        ref={triggerRef}
        className={open ? 'user-trigger open' : 'user-trigger'}
        aria-haspopup="menu"
        aria-label={`Konto und Design: ${primaryLine}`}
        aria-expanded={open}
        aria-controls={open ? menuId : undefined}
        onClick={() => setOpen((value) => !value)}
        onKeyDown={onTriggerKeyDown}
        title="Konto und Design"
      >
        <span className="avatar">
          {initials}
        </span>
        <span className="user-trigger-text">
          <span className="line-1">{primaryLine}</span>
          <span className="line-2">
            {user.roles.length === 0
              ? 'Keine Rolle'
              : user.roles.map((role) => ROLE_LABELS[role]).join(' · ')}
          </span>
        </span>
        <svg
          className="caret"
          viewBox="0 0 24 24"
          width="14"
          height="14"
          fill="none"
          stroke="currentColor"
          strokeWidth="2.2"
          strokeLinecap="round"
          strokeLinejoin="round"
          aria-hidden="true"
        >
          <path d="M6 9.5l6 6 6-6" />
        </svg>
      </button>

      {open ? (
        <div className="user-popover" id={menuId} role="menu" ref={menuRef} onKeyDown={onMenuKeyDown}>
          <div className="user-popover-head">
            <span className="avatar lg" aria-hidden="true">
              {initials}
            </span>
            <div className="who">
              {user.name?.trim() ? <strong>{user.name}</strong> : null}
              <span className="mail-line" title={user.email}>
                {user.email}
              </span>
            </div>
          </div>

          <div className="user-popover-roles">
            {user.roles.length === 0 ? (
              <span className="chip warn" title="Diesem Konto ist keine Rolle zugewiesen.">
                Keine Rolle
              </span>
            ) : (
              user.roles.map((role) => (
                <span
                  key={role}
                  className={role === 'admin' ? 'chip info' : 'chip'}
                  title={ROLE_DESCRIPTIONS[role]}
                >
                  {ROLE_LABELS[role]}
                </span>
              ))
            )}
          </div>

          <div className="user-popover-sep" role="separator" />

          <div className="user-popover-section">
            <span className="section-label">Design</span>
            <ThemePreferenceControl />
          </div>

          <div className="user-popover-sep" role="separator" />

          <button
            type="button"
            role="menuitem"
            className="user-popover-item"
            onClick={() => {
              close(false);
              onChangePassword();
            }}
          >
            <KeyIcon />
            <span>Passwort ändern</span>
          </button>

          <button
            type="button"
            role="menuitem"
            className="user-popover-item danger"
            disabled={loggingOut}
            onClick={() => {
              close(false);
              onLogout();
            }}
          >
            {loggingOut ? <Spinner label="Abmeldung läuft…" /> : <LogoutIcon />}
            <span>Abmelden</span>
          </button>
        </div>
      ) : null}
    </div>
  );
}

function KeyIcon(): ReactNode {
  return (
    <svg viewBox="0 0 24 24" width="16" height="16" fill="none" stroke="currentColor"
      strokeWidth="1.9" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
      <circle cx="8" cy="15" r="4.2" />
      <path d="M11 12L20 3M17.2 5.8l2.2 2.2M14.6 8.4l2.2 2.2" />
    </svg>
  );
}

function LogoutIcon(): ReactNode {
  return (
    <svg viewBox="0 0 24 24" width="16" height="16" fill="none" stroke="currentColor"
      strokeWidth="1.9" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
      <path d="M9.5 20H5.4A1.4 1.4 0 0 1 4 18.6V5.4A1.4 1.4 0 0 1 5.4 4h4.1M16 8l4 4-4 4M20 12H9.5" />
    </svg>
  );
}

export default UserMenu;
